/**
 * Kitchen status vocabulary for the portal's Kitchen Status page.
 *
 * The iPad POS works every value out from its own print queue and uploads the
 * result (OBKitchenStatusUploader); this page only renders it. The keys and
 * wording below mirror OBKitchenStatusView so staff read the same thing on both.
 */

const MINUTE_MS = 60 * 1000;

// A POS reports at least every 5 minutes (OBKitchenStatusUploader.heartbeatInterval).
// Past twice that its tickets may be out of date, so the page says so.
export const DEVICE_STALE_MINUTES = 10;

export const SEVERITY_LABELS = {
  counterItem: "Counter item",
  printed: "Printed",
  onDisplay: "On kitchen display",
  unknown: "Printed earlier",
  held: "Held for later",
  inProgress: "Printing…",
  missing: "Not printed",
};

/** MUI palette key per severity, as KitchenPalette picks a colour on the POS. */
export const SEVERITY_COLORS = {
  counterItem: "default",
  printed: "success",
  onDisplay: "success",
  unknown: "default",
  held: "info",
  inProgress: "warning",
  missing: "error",
};

const DOCKET_GLYPHS = {
  printed: "✓",
  printing: "…",
  waiting: "…",
  failed: "✗",
  notSent: "✗",
  recordExpired: "✓?",
  resolvedByStaff: "☑",
};

export const severityLabel = (severity) => SEVERITY_LABELS[severity] || SEVERITY_LABELS.unknown;

export const severityColor = (severity) => SEVERITY_COLORS[severity] || "default";

/** "K Chinese → Kitchen 1 ✓ · Kitchen Docket → Main ✗" */
export const docketSummary = (dockets) =>
  (dockets || [])
    .map((docket) => `${docket.label} ${DOCKET_GLYPHS[docket.state] || ""}`.trim())
    .join(" · ");

/** The chip beside a ticket's age, or null when the order was never meant for the display. */
export const kdsChip = (kdsState, hasProblems) => {
  switch (kdsState) {
    case "sent":
      return { label: "KDS ✓", color: "success" };
    case "failed":
      // A display failure on a ticket that is fine is information, not an alarm.
      return { label: "KDS failed", color: hasProblems ? "error" : "default" };
    case "pending":
      return { label: "KDS pending", color: "warning" };
    default:
      return null;
  }
};

export const hasProblems = (status) => Boolean(status && status.problems && status.problems.length > 0);

/** The last four characters staff use to talk about an order, matching the dockets. */
export const shortOrderNumber = (orderNumber) => String(orderNumber || "").slice(-4);

export const ageText = (dispatchedAt, now = Date.now()) => {
  if (!dispatchedAt) {
    return "";
  }
  const minutes = Math.floor(Math.max(0, now - dispatchedAt) / MINUTE_MS);
  if (minutes < 1) {
    return "just now";
  }
  if (minutes < 60) {
    return `${minutes} min ago`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h ago` : `${hours} h ${rest} min ago`;
};

export const isDeviceStale = (device, now = Date.now()) =>
  !device || !device.reportedAt || now - device.reportedAt > DEVICE_STALE_MINUTES * MINUTE_MS;

/**
 * The tickets to show. A resolved ticket is history, so "Problems" leaves it out
 * along with every ticket that has nothing wrong.
 */
export const visibleStatuses = (statuses, problemsOnly) =>
  problemsOnly ? (statuses || []).filter(hasProblems) : statuses || [];

/**
 * Dealt round-robin into columns so they stay about level however tall each
 * ticket is - the same layout as the POS board.
 */
export const dealIntoColumns = (items, columnCount) => {
  const count = Math.max(1, Math.min(columnCount, items.length || 1));
  const columns = Array.from({ length: count }, () => []);
  items.forEach((item, index) => columns[index % count].push(item));
  return columns;
};
